import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { Box, Button, Container, Typography } from '@mui/material';
import { useDispatch } from 'react-redux';
import { AuthModal } from '../../components/AuthModal/AuthModal';
import { useAuth } from '../../module/AuthContext';
import { toggleAuthModal } from '../../redux/authentication/action';
import { MessageDialogue } from '../../components/MessageDialogue/MessageDialogue';
import { MetaTags } from '../../components/Metatags/Metatags';
import {
    PUBLIC_CREATOR_COLLAB_BAR_SPACER,
    PublicCreatorCollabBar,
} from '../../components/PublicInfluencerProfile/PublicCreatorCollabBar';
import { CreatorMessageRequiresCampaignModal } from '../../components/PublicInfluencerProfile/CreatorMessageRequiresCampaignModal';
import { PublicInfluencerProfileView } from '../../components/PublicInfluencerProfile/PublicInfluencerProfileView';
import { usePublicInfluencerProfile } from '../../components/PublicInfluencerProfile/usePublicInfluencerProfile';
import { normalizePublicProfileUserName } from '../../services/influencerPublicProfile';
import { ROUTES } from '../../types/global/routes.types';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { MainLoading as Loading } from '../../components/Loading/MainLoading';
import { showToastMessage } from '../../utils/AppUtils';
import { canMessageCreator } from '../../utils/creatorMessageGate';
import { BRAND_LUXE_BG, BRAND_LUXE_PRIMARY, brandMeshBackground } from '../BrandDashboard/brandDashboardTokens';

export default function PublicCreatorProfilePage() {
    const { userName: rawUserName } = useParams<{ userName: string }>();
    const userName = rawUserName ? normalizePublicProfileUserName(rawUserName) : undefined;
    const { influencer, loading, error } = usePublicInfluencerProfile(userName);
    const dispatch = useDispatch();
    const { isSignedIn, user } = useAuth();
    const [messageOpen, setMessageOpen] = useState(false);
    const [campaignGateOpen, setCampaignGateOpen] = useState(false);
    const [pendingAfterLogin, setPendingAfterLogin] = useState(false);

    // Re-open the message dialogue once the user logs in mid-compose
    useEffect(() => {
        if (isSignedIn && pendingAfterLogin) {
            setPendingAfterLogin(false);
            setMessageOpen(true);
        }
    }, [isSignedIn, pendingAfterLogin]);

    const openMessageOrCampaignGate = () => {
        if (!canMessageCreator(influencer?.can_message)) {
            setCampaignGateOpen(true);
            return;
        }
        setMessageOpen(true);
    };

    // After sign-in, resume message / campaign-gate flow
    useEffect(() => {
        if (isSignedIn && pendingAfterLogin) {
            setPendingAfterLogin(false);
            openMessageOrCampaignGate();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- only resume once after auth
    }, [isSignedIn, pendingAfterLogin]);

    const handleMessageClick = () => {
        if (!isSignedIn) {
            setPendingAfterLogin(true);
            showToastMessage('You need to be logged in to send a message', 'info');
            dispatch(toggleAuthModal(true));
            return;
        }
        openMessageOrCampaignGate();
    };

    const shareUrl = useMemo(() => {
        if (!userName || typeof window === 'undefined') return undefined;
        return `${window.location.origin}${ROUTES.PUBLIC_CREATOR_PROFILE.replace(':userName', userName)}`;
    }, [userName]);

    if (loading && !influencer) {
        return <Loading />;
    }

    if (!influencer) {
        return (
            <MetaTags title="Creator profile | Thspian" description="Public creator profile">
                <Box
                    sx={{
                        minHeight: '100vh',
                        bgcolor: BRAND_LUXE_BG,
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        px: 2,
                    }}>
                    <Container maxWidth="sm" sx={{ textAlign: 'center' }}>
                        <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 22, mb: 1 }}>
                            Profile not available
                        </Typography>
                        <Typography sx={{ color: '#adaaaa', mb: 3 }}>
                            {error || 'This creator profile is not public yet or the link may be incorrect.'}
                        </Typography>
                        <Button
                            component={RouterLink}
                            to="/"
                            variant="outlined"
                            sx={{ color: BRAND_LUXE_PRIMARY, borderColor: 'rgba(255,201,101,0.4)' }}>
                            Go home
                        </Button>
                    </Container>
                </Box>
            </MetaTags>
        );
    }

    const title = `${influencer.name ?? userName} | Creator profile`;
    const description = influencer.bio?.slice(0, 160) || `View ${influencer.name}'s public creator profile on Thspian.`;

    return (
        <MetaTags title={title} description={description}>
            <Box
                sx={{
                    minHeight: '100vh',
                    width: '100%',
                    ...brandMeshBackground(BRAND_LUXE_BG),
                    pb: PUBLIC_CREATOR_COLLAB_BAR_SPACER,
                }}>
                <PublicInfluencerProfileView
                    influencer={influencer}
                    shareUrl={shareUrl}
                    layout="page"
                    onMessage={handleMessageClick}
                    isOwner={Boolean(
                        isSignedIn &&
                        user?._id &&
                        influencer.created_by &&
                        (typeof influencer.created_by === 'string'
                            ? influencer.created_by === user._id
                            : (influencer.created_by as any)?._id === user._id),
                    )}
                />
                <PublicCreatorCollabBar influencer={influencer} />
                <AuthModal />
                <CreatorMessageRequiresCampaignModal
                    open={campaignGateOpen}
                    onClose={() => setCampaignGateOpen(false)}
                    creatorName={influencer.name}
                />
                <MessageDialogue
                    open={messageOpen}
                    onClose={() => setMessageOpen(false)}
                    recipient={
                        typeof influencer.created_by === 'object' && influencer.created_by !== null
                            ? ({
                                  _id: influencer.created_by._id ?? '',
                                  firstName: influencer.created_by.firstName ?? influencer.name ?? '',
                                  lastName: influencer.created_by.lastName ?? '',
                                  profile_picture:
                                      influencer.created_by.profile_picture ?? influencer.profile_picture ?? '',
                                  verified: influencer.created_by.verified ?? false,
                                  role: '',
                                  audme_profile: '',
                              } as any)
                            : undefined
                    }
                    onBeforeSend={() => {
                        if (!isSignedIn) {
                            setMessageOpen(false);
                            setPendingAfterLogin(true);
                            showToastMessage('You need to be logged in to send a message', 'info');
                            dispatch(toggleAuthModal(true));
                            return false;
                        }
                        if (!canMessageCreator(influencer.can_message)) {
                            setMessageOpen(false);
                            setCampaignGateOpen(true);
                            return false;
                        }
                        return true;
                    }}
                />
            </Box>
        </MetaTags>
    );
}
